import React, { useCallback, useEffect, useState } from 'react';
import { authAPI, hierarchyAPI } from '../api/client';
import HierarchyNodeTreeMultiSelect from '../components/HierarchyNodeTreeMultiSelect';
import DelegationsPanel from '../components/DelegationsPanel';
import BookmarksPanel from '../components/BookmarksPanel';
import NotificationPreferencesPanel from '../components/NotificationPreferencesPanel';
import { extractErrorMessage } from '../utils/errors';
import { useAuth } from '../auth/AuthContext';

/**
 * Self-service profile: change your own password, and set your own default
 * location(s) (issue #17) -- the self-service counterpart to UserModal's
 * "Default location(s)" section, which is for an ADMIN setting someone else's.
 * No users.* permission is required for anything on this page; both actions
 * are gated on require_identity alone (see routers/auth.py's /me/home-nodes
 * and /change-password).
 *
 * Didn't exist before this: authAPI.changePassword was already wired into
 * client.js but nothing ever called it, since there was no profile screen at
 * all -- only an admin's set-password flow in UsersPage.
 */
function ProfilePage() {
  const {
    user, homeScope, impersonating, refresh, delegation, startDelegation,
  } = useAuth();

  const [nodes, setNodes] = useState([]);
  const [selection, setSelection] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingScope, setSavingScope] = useState(false);
  const [scopeError, setScopeError] = useState(null);
  const [scopeSaved, setScopeSaved] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSaved, setPasswordSaved] = useState(false);

  // Delegations (issue #44): what I have lent out, and what has been lent to me.
  const [granted, setGranted] = useState([]);
  const [received, setReceived] = useState([]);
  const [delegationBusy, setDelegationBusy] = useState(false);
  const [delegationError, setDelegationError] = useState(null);

  const loadDelegations = useCallback(() => {
    Promise.all([authAPI.getMyDelegations(), authAPI.getDelegationsToMe()])
      .then(([mine, theirs]) => {
        setGranted(mine.data);
        setReceived(theirs.data);
      })
      .catch((err) => setDelegationError(extractErrorMessage(err)));
  }, []);

  useEffect(() => { loadDelegations(); }, [loadDelegations]);

  const handleCreateDelegation = async (payload) => {
    setDelegationBusy(true);
    setDelegationError(null);
    try {
      await authAPI.createMyDelegation(payload);
      loadDelegations();
    } catch (err) {
      setDelegationError(extractErrorMessage(err));
    } finally {
      setDelegationBusy(false);
    }
  };

  const handleRevokeDelegation = async (d) => {
    setDelegationBusy(true);
    setDelegationError(null);
    try {
      await authAPI.revokeMyDelegation(d.id);
      loadDelegations();
    } catch (err) {
      setDelegationError(extractErrorMessage(err));
    } finally {
      setDelegationBusy(false);
    }
  };

  const handleActAs = async (d) => {
    setDelegationBusy(true);
    setDelegationError(null);
    try {
      await startDelegation(d.id);
    } catch (err) {
      setDelegationError(extractErrorMessage(err));
    } finally {
      setDelegationBusy(false);
    }
  };

  useEffect(() => {
    hierarchyAPI.getNodes().then((res) => setNodes(res.data)).finally(() => setLoading(false));
  }, []);

  // Seed the picker from /me's home_scope once both the node list and that
  // scope have loaded -- homeScope reflects the EFFECTIVE user, so this page
  // correctly shows (and, while impersonating, refuses to edit) the
  // impersonated person's own locations.
  useEffect(() => {
    const seed = {};
    (homeScope?.nodes || []).forEach((h) => {
      seed[h.hierarchy_node_id] = h.include_descendants;
    });
    setSelection(seed);
  }, [homeScope]);

  const handleSaveScope = async (e) => {
    e.preventDefault();
    setSavingScope(true);
    setScopeError(null);
    setScopeSaved(false);
    try {
      const payload = Object.entries(selection).map(([id, includeDescendants]) => ({
        hierarchy_node_id: Number(id),
        include_descendants: includeDescendants,
      }));
      await authAPI.setMyHomeNodes(payload);
      await refresh();
      setScopeSaved(true);
    } catch (err) {
      setScopeError(extractErrorMessage(err));
    } finally {
      setSavingScope(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSaved(false);
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordSaved(true);
    } catch (err) {
      setPasswordError(extractErrorMessage(err));
    }
  };

  if (loading) {
    return <div className="loading">Loading profile...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h2>👤 My Profile</h2>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <h3>⭐ My Bookmarks</h3>
        <p style={{ fontSize: '13px', color: 'var(--muted)', marginTop: 0 }}>
          Policies you've pinned as pertinent to you. Bookmark one from its page,
          then group them into folders here.
        </p>
        <BookmarksPanel />
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <h3>🔔 Notifications</h3>
        <p style={{ fontSize: '13px', color: 'var(--muted)', marginTop: 0 }}>
          Email you'd like to receive. Everything is off unless you turn it on
          here — and whether or not you do, these items still appear in My Tasks.
        </p>
        <NotificationPreferencesPanel />
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <h3>Default location(s)</h3>
        <p style={{ fontSize: '13px', color: 'var(--muted)', marginTop: 0 }}>
          Where your Documents list defaults to. Organization-level policies above
          a selected location are always included; you can switch to "All
          locations" on the Documents page at any time.
        </p>
        {impersonating && (
          <div className="alert alert-warning" style={{ marginBottom: '12px' }}>
            You are impersonating {user?.full_name}. Home-location changes are
            blocked while impersonating -- stop impersonating first.
          </div>
        )}
        <form onSubmit={handleSaveScope}>
          <div className="form-group">
            <HierarchyNodeTreeMultiSelect
              nodes={nodes}
              selection={selection}
              onChange={setSelection}
              defaultIncludeDescendants
              emptyMessage="No hierarchy locations exist yet."
            />
          </div>
          {scopeError && <div className="alert alert-error">{scopeError}</div>}
          {scopeSaved && !scopeError && (
            <div className="alert alert-success">Default location(s) saved.</div>
          )}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={savingScope || impersonating}
          >
            {savingScope ? 'Saving...' : 'Save default location(s)'}
          </button>
        </form>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <h3>Delegates I've granted</h3>
        <p style={{ fontSize: '13px', color: 'var(--muted)', marginTop: 0 }}>
          People who may act on your behalf while you are away. They stay signed in
          as themselves and gain only what you tick below — never the rest of your
          access. Revoking takes effect on their very next action.
        </p>
        {impersonating && (
          <div className="alert alert-warning" style={{ marginBottom: '12px' }}>
            Delegation changes are blocked while impersonating — stop
            impersonating first.
          </div>
        )}
        {delegation && (
          <div className="alert alert-warning" style={{ marginBottom: '12px' }}>
            Delegation changes are blocked while you are acting for someone else
            — stop acting first.
          </div>
        )}
        {delegationError && <div className="alert alert-error">{delegationError}</div>}
        <DelegationsPanel
          delegations={granted}
          onCreate={handleCreateDelegation}
          onRevoke={handleRevokeDelegation}
          busy={delegationBusy}
          disabled={impersonating || !!delegation}
        />
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <h3>Acting for others</h3>
        <p style={{ fontSize: '13px', color: 'var(--muted)', marginTop: 0 }}>
          Delegations other people have granted you. Choosing "Act as" does not
          sign you in as them — you stay yourself, and only gain what their
          delegation lends.
        </p>
        {received.filter((d) => d.is_live).length === 0 ? (
          <div className="empty-state" style={{ padding: '12px 0' }}>
            Nobody has delegated to you.
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>On behalf of</th>
                  <th>You may act on</th>
                  <th>Reason</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {received.filter((d) => d.is_live).map((d) => (
                  <tr key={d.id}>
                    <td>{d.delegator_name}</td>
                    <td>
                      {[d.can_act_on_approvals && 'Approvals',
                        d.can_edit_owned_documents && 'Owned documents']
                        .filter(Boolean).join(', ')}
                    </td>
                    <td>{d.reason || '—'}</td>
                    <td>
                      <button
                        className="btn btn-primary btn-small"
                        onClick={() => handleActAs(d)}
                        disabled={delegationBusy || !!delegation || impersonating}
                      >
                        Act as {d.delegator_name}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h3>Change password</h3>
        <p style={{ fontSize: '13px', color: 'var(--muted)', marginTop: 0 }}>
          At least 12 characters. Only for accounts using local (non-SSO) sign-in.
        </p>
        <form onSubmit={handleChangePassword}>
          <div className="form-group">
            <label>Current password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>
          <div className="form-group">
            <label>New password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <div className="form-group">
            <label>Confirm new password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          {passwordError && <div className="alert alert-error">{passwordError}</div>}
          {passwordSaved && !passwordError && (
            <div className="alert alert-success">Password changed.</div>
          )}
          <button type="submit" className="btn btn-primary">
            Change password
          </button>
        </form>
      </div>
    </div>
  );
}

export default ProfilePage;
